import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import { VoicePreferencesSection } from "@/components/voice-preferences-section";
import { VoiceAccuracyMeter } from "@/components/voice-accuracy-meter";
import { MicRecordingPanel } from "@/components/mic-recording-panel";
import { VoiceSampleList } from "@/components/voice-sample-list";
import { TranscriptionProgress } from "@/components/transcription-progress";
import { MediaUploadPanel } from "@/components/media-upload-panel";
import { VoiceStatsCard } from "@/components/voice-stats-card";
import { VoiceMilestoneProgress } from "@/components/voice-milestone-progress";
import { motion, AnimatePresence } from "framer-motion";
import {
  useGetVoiceProfile,
  useTrainVoice,
  useTrainVoiceAudio,
  useDeleteAudioTranscript,
  useImportUrl,
  useResetVoiceProfile,
} from "@workspace/api-client-react";
import { useAppStore } from "@/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import {
  Loader2,
  BrainCircuit,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  PenTool,
  AudioLines,
  Zap,
  MessageSquareQuote,
  Target,
  Link,
  ExternalLink,
  ArrowRight,
  TrendingUp,
  Trash2,
  RotateCcw,
  Upload,
  FileText,
  ShieldCheck,
  RefreshCw,
  X,
  Lightbulb,
  ChevronDown,
  Lock,
} from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL ?? "/";

const MILESTONES: Array<{ count: number; label: string; description: string }> = [
  { count: 3, label: "AI Generation Enabled", description: "The AI can now generate posts in your voice!" },
  { count: 5, label: "Cadence & Punctuation Fingerprinting", description: "Richer cadence and punctuation fingerprinting unlocked." },
  { count: 8, label: "Voice State Clustering", description: "Voice State Clustering unlocked — we can now detect your distinct writing modes." },
  { count: 15, label: "Full Sentence Energy Arc", description: "Full sentence energy arc and clause structure fingerprints unlocked." },
  { count: 20, label: "Maximum Voice Fidelity", description: "Maximum voice fidelity achieved — your AI twin is fully calibrated." },
];

const SUGGESTION_CHIPS = [
  "Paste a long text you wrote",
  "Paste an old email",
  "Paste a tweet thread",
  "Just ramble about anything",
];

async function uploadTextFile(file: File, sessionId: string): Promise<{ samplesCreated: number; sampleCount: number; message: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const apiBase = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
  const res = await fetch(`${apiBase}/api/voice/train-file`, {
    method: "POST",
    headers: { "x-session-id": sessionId },
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Failed to upload file");
  return json;
}

const DNA_DESCRIPTIONS: Record<string, string> = {
  "Primary Tone": "The overall emotional register of your writing — e.g. warm, assertive, analytical.",
  "Vocabulary": "How simple or advanced your word choices tend to be.",
  "Emoji Usage": "How often emojis appear in your writing.",
  "Humor": "How much wit or levity you naturally weave in.",
  "Story Pattern": "Whether you lead with narrative, data, opinion, or questions.",
  "Sentence Rhythm": "Your mix of short punchy sentences vs. longer flowing ones.",
  "Formatting": "How you structure your posts — bullets, paragraphs, or headers.",
  "Typing Feel": "Whether your writing feels polished, conversational, or stream-of-consciousness.",
  "Capitalization": "Any non-standard capitalization habits (all-caps words, lowercase-everywhere, etc.).",
};

type TabKey = "text" | "audio";

export default function VoiceTraining() {
  const { sessionId } = useAppStore();
  const { toast } = useToast();
  const search = useSearch();
  const [samples, setSamples] = useState<string[]>([""]);
  const [sampleErrors, setSampleErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>("text");

  useEffect(() => {
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    if (tab === "audio") setActiveTab("audio");
    else if (tab === "text") setActiveTab("text");
  }, [search]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importUrl, setImportUrl] = useState("");
  const [importStatus, setImportStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [importError, setImportError] = useState("");

  const [trainSuccess, setTrainSuccess] = useState(false);
  const [moreWaysOpen, setMoreWaysOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [lastCapturedTraits, setLastCapturedTraits] = useState<{ cadence?: string; tone?: string; fillerWordCadence?: string; typingFeel?: string; emotionalRegister?: string; } | null>(null);
  const [capturedCardDismissed, setCapturedCardDismissed] = useState(false);

  type AdversarialExcerpt = { id: number; text: string };
  type AdversarialTestState =
    | { phase: "idle" }
    | { phase: "loading" }
    | { phase: "active"; excerpts: AdversarialExcerpt[]; explanation: string }
    | { phase: "result"; correct: boolean; weakAreas: string[]; stats: { attempts: number; correct: number }; selectedIndex: number; correctIndex: number; explanation: string };

  const [adversarialTest, setAdversarialTest] = useState<AdversarialTestState>({ phase: "idle" });
  const [adversarialError, setAdversarialError] = useState<string | null>(null);
  const [selectedVoiceStateTraining, setSelectedVoiceStateTraining] = useState<string | null>(null);
  const [dismissedNudges, setDismissedNudges] = useState<Set<string>>(new Set());
  const prevSampleCountRef = useRef<number | null>(null);

  const { data: profile, isLoading, refetch } = useGetVoiceProfile({
    request: { headers: { "x-session-id": sessionId } },
  });

  useEffect(() => {
    const currentCount = profile?.sampleCount ?? 0;
    if (prevSampleCountRef.current !== null && currentCount > prevSampleCountRef.current) {
      const crossed = MILESTONES.filter(
        (m) => prevSampleCountRef.current! < m.count && currentCount >= m.count
      );
      crossed.forEach((m) => {
        toast({
          title: `${m.label} unlocked!`,
          description: m.description,
        });
      });
    }
    prevSampleCountRef.current = currentCount;
  }, [profile?.sampleCount]);

  const sessionHeaders = { headers: { "x-session-id": sessionId } };

  const resetMutation = useResetVoiceProfile({
    mutation: {
      onSuccess: () => {
        toast({ title: "Voice profile reset", description: "Your samples and analysis have been cleared. Your voice preferences are unchanged." });
        refetch();
        setResetConfirmOpen(false);
      },
      onError: (err: any) => {
        toast({ title: "Reset failed", description: err.message || "Could not reset profile.", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const trainMutation = useTrainVoice({
    mutation: {
      onSuccess: () => {
        refetch();
        setTrainSuccess(true);
        setTimeout(() => setTrainSuccess(false), 8000);
      },
      onError: (err: any) => {
        toast({ title: "Training failed", description: err.message || "Failed to process samples.", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const trainAudioMutation = useTrainVoiceAudio({
    mutation: {
      onSuccess: (data) => {
        toast({ title: "Audio Analyzed", description: "Transcribed and added to your profile." });
        refetch();
        if (data.analysis) {
          const a = data.analysis as Record<string, unknown>;
          setLastCapturedTraits({
            cadence: typeof a.cadence === "string" ? a.cadence : undefined,
            tone: typeof a.tone === "string" ? a.tone : undefined,
            fillerWordCadence: typeof a.fillerWordCadence === "string" && a.fillerWordCadence !== "none detected" ? a.fillerWordCadence : undefined,
            typingFeel: typeof a.typingFeel === "string" ? a.typingFeel : undefined,
            emotionalRegister: typeof a.emotionalRegister === "string" ? a.emotionalRegister : undefined,
          });
          setCapturedCardDismissed(false);
        }
      },
      onError: (err: any) => {
        toast({ title: "Audio training failed", description: err.message || "Failed to process audio.", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const deleteMutation = useDeleteAudioTranscript({
    mutation: {
      onSuccess: () => {
        toast({ title: "Transcript removed" });
        refetch();
      },
      onError: (err: any) => {
        toast({ title: "Delete failed", description: err.message, variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const importUrlMutation = useImportUrl({
    mutation: {
      onSuccess: (data) => {
        const firstEmpty = samples.findIndex((s) => s.trim().length === 0);
        if (firstEmpty >= 0) {
          updateSample(firstEmpty, data.text);
        } else {
          setSamples([...samples, data.text]);
        }
        setImportStatus("success");
        setImportUrl("");
        toast({
          title: "Post imported!",
          description: `Extracted ${data.charCount} characters from ${data.platform}.`,
        });
        setTimeout(() => setImportStatus("idle"), 3000);
      },
      onError: (err: any) => {
        const msg = err?.message || err?.error || "Could not import from that URL. Make sure the post is public.";
        setImportError(msg);
        setImportStatus("error");
      },
    },
    request: sessionHeaders,
  });

  const handleImportUrl = () => {
    if (!importUrl.trim()) return;
    setImportStatus("loading");
    setImportError("");
    importUrlMutation.mutate({ data: { url: importUrl.trim() } });
  };

  const MAX_BLOB_BYTES = 200 * 1024 * 1024;
  const audioReaderRef = useRef<FileReader | null>(null);

  const cancelUpload = () => {
    if (audioReaderRef.current) {
      audioReaderRef.current.abort();
      audioReaderRef.current = null;
    }
    trainAudioMutation.reset();
  };

  const sendAudioBlob = (blob: Blob) => {
    if (blob.size > MAX_BLOB_BYTES) {
      toast({
        title: "Recording too large",
        description: `This recording is ${Math.round(blob.size / 1024 / 1024)}MB. Max is 200MB — try trimming it down.`,
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    audioReaderRef.current = reader;
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      audioReaderRef.current = null;
      if (reader.result) {
        const base64 = (reader.result as string).split(",")[1];
        trainAudioMutation.mutate({ data: { audioBase64: base64, sessionId } });
      }
    };
    reader.onerror = () => {
      audioReaderRef.current = null;
      toast({ title: "Read error", description: "Could not read the audio file. Please try again.", variant: "destructive" });
    };
  };

  const recorder = useAudioRecorder({
    onRecordingComplete: sendAudioBlob,
  });

  const TEXT_MIN_WORD_COUNT = 40;
  const TEXT_MIN_CHARS = 50;
  const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

  const handleTrain = () => {
    const errors = samples.map((s) => {
      const trimmed = s.trim();
      if (trimmed.length === 0) return "";
      if (trimmed.length < TEXT_MIN_CHARS) return `Please write at least ${TEXT_MIN_CHARS} characters so we can learn your voice patterns.`;
      const wc = countWords(trimmed);
      if (wc < TEXT_MIN_WORD_COUNT) return "This sample is too short — paste at least a sentence or two so we can pick up your voice patterns.";
      return "";
    });
    setSampleErrors(errors);

    const hasInlineError = errors.some((e) => e.length > 0);
    if (hasInlineError) return;

    const validSamples = samples.filter((s) => countWords(s.trim()) >= TEXT_MIN_WORD_COUNT);
    if (validSamples.length < 1) {
      toast({ title: "Not enough data", description: "Please provide at least one substantial writing sample.", variant: "destructive" });
      return;
    }
    trainMutation.mutate({ data: { samples: validSamples, sessionId } });
  };

  const updateSample = (index: number, value: string) => {
    const newSamples = [...samples];
    newSamples[index] = value;
    setSamples(newSamples);
  };

  const addSampleBox = () => setSamples([...samples, ""]);

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [fileUploadStatus, setFileUploadStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [fileUploadMsg, setFileUploadMsg] = useState("");
  const textFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleTextFileDrop = useCallback(async (file: File) => {
    const allowed = ["text/plain", "text/markdown", "application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const byExt = file.name.match(/\.(txt|md|pdf|docx)$/i);
    if (!allowed.includes(file.type) && !byExt) {
      toast({ title: "Unsupported file", description: "Upload a .txt, .md, .pdf, or .docx file.", variant: "destructive" });
      return;
    }
    setFileUploadStatus("loading");
    setFileUploadMsg("");
    try {
      const result = await uploadTextFile(file, sessionId);
      setFileUploadStatus("success");
      setFileUploadMsg(`${result.samplesCreated} sample${result.samplesCreated !== 1 ? "s" : ""} extracted from "${file.name}".`);
      refetch();
      setTimeout(() => setFileUploadStatus("idle"), 5000);
    } catch (err: any) {
      setFileUploadStatus("error");
      setFileUploadMsg(err.message || "Upload failed. Try again.");
    }
  }, [sessionId, refetch, toast]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    if (!isAudio && !isVideo) {
      toast({ title: "Invalid file", description: "Please upload an audio or video file (mp3, m4a, wav, webm, mp4, mov).", variant: "destructive" });
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max file size is 200MB.", variant: "destructive" });
      return;
    }
    sendAudioBlob(file);
    e.target.value = "";
  };

  const [, navigate] = useLocation();
  const analysis = profile?.analysis as Record<string, any> | null | undefined;
  const audioTranscripts = (profile?.audioTranscripts as string[] | undefined) ?? [];
  const sampleCount = profile?.sampleCount ?? 0;

  return (
    <>
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_380px] gap-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-display font-bold">
            {sampleCount === 0 && !isLoading ? "Let's build your AI voice" : "Build your AI voice"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {sampleCount === 0 && !isLoading
              ? "Paste 3 writing samples — emails, posts, texts, anything you've written. That's all it takes. The AI does the rest."
              : "Paste 3 samples of anything you've written. The AI learns your tone, rhythm, and style — then writes like you."}
          </p>
        </div>

        {/* Post-train success banner */}
        <AnimatePresence>
          {trainSuccess && sampleCount >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/8"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <p className="text-sm text-foreground flex-1">
                Voice sample added!{" "}
                <button
                  onClick={() => navigate("/studio")}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  Generate your first post →
                </button>
              </p>
              <button onClick={() => setTrainSuccess(false)} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" aria-label="Dismiss">
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex gap-1 bg-secondary/50 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab("audio")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all relative ${
              activeTab === "audio" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <AudioLines className="w-4 h-4" /> Audio Samples
            <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-primary text-primary-foreground leading-none whitespace-nowrap">
              Most Accurate
            </span>
          </button>
          <button
            onClick={() => setActiveTab("text")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "text" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <PenTool className="w-4 h-4" /> Text Samples
          </button>
        </div>

        {activeTab === "text" && (
          <div className="space-y-5">
            {/* Suggestion chips */}
            <div className="flex flex-wrap gap-2">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => updateSample(0, chip + " ")}
                  className="text-xs px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>

            {/* Voice state filter — only shown for returning users with voice states */}
            {(() => {
              const voiceStates = analysis && (analysis as Record<string, unknown>).voiceStates as Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;
              if (!voiceStates || voiceStates.length === 0) return null;
              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Zap className="w-3 h-3" /> Filter by writing mode <span className="text-muted-foreground/60 font-normal">(select to view samples for a specific detected mode)</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setSelectedVoiceStateTraining(null)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selectedVoiceStateTraining === null
                          ? "bg-secondary text-foreground border-border"
                          : "bg-transparent text-muted-foreground border-border/50 hover:border-border"
                      }`}
                    >
                      All modes
                    </button>
                    {voiceStates.map((vs) => (
                      <button
                        key={vs.label}
                        onClick={() => setSelectedVoiceStateTraining(vs.label === selectedVoiceStateTraining ? null : vs.label)}
                        title={`${vs.description} — Best for: ${vs.exampleContext}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selectedVoiceStateTraining === vs.label
                            ? "bg-primary/15 text-primary border-primary/40"
                            : "bg-transparent text-muted-foreground border-border/50 hover:border-border hover:text-foreground"
                        }`}
                      >
                        {vs.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Sample textareas */}
            {(() => {
              const sampleStateAssignments = analysis && (analysis as Record<string, unknown>).sampleStateAssignments as string[] | undefined;
              return samples.map((sample, idx) => {
                const assignedState = sampleStateAssignments?.[idx];
                if (selectedVoiceStateTraining && assignedState && assignedState !== selectedVoiceStateTraining) return null;
                const wc = countWords(sample);
                const hasContent = sample.trim().length > 0;
                const isShort = hasContent && wc < 80;
                const error = sampleErrors[idx] || "";
                const isFirstAndNew = idx === 0 && sampleCount === 0 && !isLoading;
                return (
                <div key={idx} className="space-y-1.5">
                  <label className="text-sm font-medium text-muted-foreground flex justify-between items-center">
                    <span>Sample {idx + 1}{assignedState && <span className="ml-2 text-xs text-primary/70 font-normal">{assignedState}</span>}</span>
                    {hasContent && (
                      <span
                        data-testid={`sample-word-count-${idx}`}
                        className={`text-xs px-1.5 py-0.5 rounded font-medium ${isShort ? "bg-amber-500/15 text-amber-400" : "text-muted-foreground"}`}
                      >
                        {wc} words{isShort ? " — Add more" : ""}
                      </span>
                    )}
                  </label>
                  <Textarea
                    value={sample}
                    onChange={(e) => {
                      updateSample(idx, e.target.value);
                      const trimmed = e.target.value.trim();
                      const newErrors = [...sampleErrors];
                      if (trimmed.length > 0 && trimmed.length < TEXT_MIN_CHARS) {
                        newErrors[idx] = `Please write at least ${TEXT_MIN_CHARS} characters so we can learn your voice patterns.`;
                      } else {
                        newErrors[idx] = "";
                      }
                      setSampleErrors(newErrors);
                    }}
                    placeholder="Paste something you've written — an email draft, a tweet, a Slack message, a rant. Anything works."
                    className={`bg-card focus-visible:ring-primary rounded-xl resize-y ${isFirstAndNew ? "min-h-[160px]" : "min-h-[120px]"} ${error ? "border-destructive" : "border-border"}`}
                  />
                  {error && (
                    <p className="text-xs text-destructive" data-testid={`sample-error-${idx}`}>
                      {error}
                    </p>
                  )}
                </div>
              );
              });
            })()}

            {/* Add another sample */}
            <Button variant="outline" onClick={addSampleBox} disabled={trainMutation.isPending} className="rounded-xl border-dashed w-full sm:w-auto">
              + Add another sample
            </Button>

            {/* Sticky train button */}
            <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm py-3 -mx-1 px-1 border-t border-border/40 z-10">
              <Button
                onClick={handleTrain}
                disabled={trainMutation.isPending || samples.some((s) => s.trim().length > 0 && s.trim().length < TEXT_MIN_CHARS)}
                className="w-full rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20"
              >
                {trainMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing Voice...
                  </>
                ) : (
                  <>
                    <BrainCircuit className="w-4 h-4 mr-2" /> Save & Analyse Voice
                  </>
                )}
              </Button>
            </div>

            <TranscriptionProgress isActive={trainMutation.isPending} variant="training" />

            {/* "More ways to add" disclosure */}
            <div>
              <button
                type="button"
                onClick={() => setMoreWaysOpen((v) => !v)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${moreWaysOpen ? "rotate-180" : ""}`} />
                Upload a file or import from a URL instead
              </button>

              <AnimatePresence>
                {moreWaysOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.22 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-4 pt-4">
                      {/* File drop zone */}
                      <div
                        data-testid="file-drop-zone"
                        onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                        onDragLeave={() => setIsDraggingFile(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingFile(false);
                          const file = e.dataTransfer.files[0];
                          if (file) handleTextFileDrop(file);
                        }}
                        onClick={() => textFileInputRef.current?.click()}
                        className={`relative flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                          isDraggingFile
                            ? "border-primary bg-primary/10 shadow-[0_0_20px_4px_hsl(188_90%_42%/0.2)]"
                            : "border-border/60 bg-card/30 hover:border-primary/40 hover:bg-primary/5"
                        }`}
                      >
                        <input
                          ref={textFileInputRef}
                          type="file"
                          accept=".txt,.md,.pdf,.docx"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleTextFileDrop(file);
                            e.target.value = "";
                          }}
                        />
                        {fileUploadStatus === "loading" ? (
                          <>
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                            <p className="text-sm text-muted-foreground">Extracting text from file...</p>
                          </>
                        ) : fileUploadStatus === "success" ? (
                          <>
                            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                            <p className="text-sm text-emerald-400">{fileUploadMsg}</p>
                          </>
                        ) : fileUploadStatus === "error" ? (
                          <>
                            <AlertCircle className="w-6 h-6 text-destructive" />
                            <p className="text-sm text-destructive">{fileUploadMsg}</p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <Upload className="w-5 h-5 text-primary" />
                              <FileText className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-foreground">Drop a file or click to browse</p>
                              <p className="text-xs text-muted-foreground mt-0.5">.txt, .md, .pdf, .docx — long texts auto-split into multiple samples</p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* URL import */}
                      <div className="card-glow bg-card/50 border border-border rounded-2xl p-5 space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <Link className="w-4 h-4 text-primary" />
                          Import from URL
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Paste a link to a public Twitter/X, LinkedIn, Reddit, or Instagram post and we'll extract the text automatically.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="url"
                            value={importUrl}
                            onChange={(e) => {
                              setImportUrl(e.target.value);
                              if (importStatus === "error") setImportStatus("idle");
                            }}
                            onKeyDown={(e) => e.key === "Enter" && handleImportUrl()}
                            placeholder="https://twitter.com/user/status/..."
                            className="flex-1 h-10 px-3 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                          <Button
                            onClick={handleImportUrl}
                            disabled={importStatus === "loading" || !importUrl.trim()}
                            className="rounded-xl px-5 h-10 shrink-0"
                          >
                            {importStatus === "loading" ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <ExternalLink className="w-4 h-4 mr-1.5" /> Import
                              </>
                            )}
                          </Button>
                        </div>
                        <AnimatePresence>
                          {importStatus === "success" && (
                            <motion.div
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="flex items-center gap-2 text-xs text-emerald-400"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Text extracted and added to a sample box below.
                            </motion.div>
                          )}
                          {importStatus === "error" && (
                            <motion.div
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="flex items-center gap-2 text-xs text-destructive"
                            >
                              <AlertCircle className="w-3.5 h-3.5" /> {importError || "Could not import from that URL. Make sure the post is public."}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        {activeTab === "audio" && (
          <>
            <MicRecordingPanel
              recorder={recorder}
              trainAudioMutation={trainAudioMutation}
              onFileUpload={handleFileUpload}
              fileInputRef={fileInputRef}
              onCancelUpload={cancelUpload}
            />

            <AnimatePresence>
              {lastCapturedTraits && !capturedCardDismissed && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-sm font-semibold text-foreground">What we captured from your audio</span>
                    </div>
                    <button
                      onClick={() => setCapturedCardDismissed(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      aria-label="Dismiss"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {lastCapturedTraits.tone && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 capitalize">
                        Tone: {lastCapturedTraits.tone}
                      </span>
                    )}
                    {lastCapturedTraits.cadence && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 capitalize">
                        Cadence: {lastCapturedTraits.cadence}
                      </span>
                    )}
                    {lastCapturedTraits.typingFeel && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-foreground border border-border capitalize">
                        Speaking feel: {lastCapturedTraits.typingFeel.replace(/-/g, " ")}
                      </span>
                    )}
                    {lastCapturedTraits.fillerWordCadence && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Verbal tics: {lastCapturedTraits.fillerWordCadence.length > 40 ? lastCapturedTraits.fillerWordCadence.slice(0, 40) + "…" : lastCapturedTraits.fillerWordCadence}
                      </span>
                    )}
                    {lastCapturedTraits.emotionalRegister && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-foreground border border-border">
                        {lastCapturedTraits.emotionalRegister.length > 50 ? lastCapturedTraits.emotionalRegister.slice(0, 50) + "…" : lastCapturedTraits.emotionalRegister}
                      </span>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="border-t border-border/60 pt-6">
              <MediaUploadPanel onSuccess={() => refetch()} />
            </div>

            <VoiceSampleList audioTranscripts={audioTranscripts} deleteMutation={deleteMutation} />
          </>
        )}

      </div>

      <div className="space-y-6">
        <AnimatePresence>
          {sampleCount >= 1 && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.97 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-2xl border border-primary/30 overflow-hidden"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.08), hsl(205 88% 52% / 0.05))" }}
            >
              {sampleCount >= 3 ? (
                <div className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-base text-foreground">You're ready — let's create something.</h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        Your AI voice is trained. Head to Studio and generate your first post.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/studio")}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-primary/35 hover:brightness-110 active:scale-[0.98]"
                    style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                  >
                    Open Studio <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-base text-foreground">Good start — keep going!</h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {`${sampleCount} sample${sampleCount === 1 ? "" : "s"} added. Add ${3 - sampleCount} more to unlock AI generation.`}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 pl-[52px]">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <TrendingUp className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                      <span>Use your highest-performing posts as samples for the best results</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Zap className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                      <span>3+ samples is the sweet spot — 5 or more is even better</span>
                    </div>
                  </div>
                  <div className="pl-[52px]">
                    <button
                      onClick={() => navigate("/studio")}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 hover:brightness-110"
                      style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                    >
                      Try Studio Anyway <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unlock teaser — shown only for new users with 0 samples */}
        {sampleCount === 0 && !isLoading && (
          <div className="rounded-2xl border border-border bg-card/50 p-5 space-y-4">
            <h3 className="font-display font-bold text-sm text-foreground">What you unlock as you train</h3>
            <div className="space-y-3">
              {[
                { count: 3, label: "AI generation enabled", icon: Sparkles },
                { count: 5, label: "Cadence fingerprinting", icon: Zap },
                { count: 8, label: "Voice mode detection", icon: AudioLines },
                { count: 20, label: "Maximum fidelity", icon: TrendingUp },
              ].map(({ count, label, icon: Icon }) => (
                <div key={count} className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-7 h-7 rounded-lg bg-secondary/60 flex items-center justify-center shrink-0">
                    <Lock className="w-3 h-3 opacity-40" />
                  </div>
                  <span className="text-xs">{count} samples — {label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <VoiceMilestoneProgress sampleCount={sampleCount} milestones={MILESTONES} />

        {profile?.stats && !isLoading && (
          <VoiceStatsCard
            stats={profile.stats}
            analysis={profile.analysis as any}
            sampleCount={profile.sampleCount ?? 0}
          />
        )}

        {/* Coaching nudge cards */}
        {!isLoading && sampleCount >= 1 && (() => {
          const nudges = profile?.coachingNudges;
          if (!nudges || nudges.length === 0) return null;
          const visible = nudges.filter((n) => !dismissedNudges.has(n.id));
          if (visible.length === 0) return null;
          return (
            <div className="space-y-2">
              {visible.map((nudge) => (
                <motion.div
                  key={nudge.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  className="flex items-start gap-3 p-3.5 rounded-2xl border border-amber-400/25 bg-amber-400/5"
                >
                  <Lightbulb className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground/80 leading-relaxed flex-1">{nudge.message}</p>
                  <button
                    onClick={() => setDismissedNudges((prev) => new Set([...prev, nudge.id]))}
                    className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              ))}
            </div>
          );
        })()}

        {/* Adversarial Voice Test card — shown when 5+ samples */}
        {!isLoading && sampleCount >= 5 && (
          <div className="bg-card rounded-3xl p-6 border border-border shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-bold text-base text-foreground">Voice Authenticity Test</h3>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Can you spot which excerpt sounds like you? We'll show you 3 paragraphs — one written in your voice, two written by generic AI.
                </p>
              </div>
            </div>

            {adversarialError && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs mb-3">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {adversarialError}
              </div>
            )}

            {adversarialTest.phase === "idle" && (
              <Button
                size="sm"
                variant="outline"
                className="w-full rounded-xl gap-2"
                onClick={async () => {
                  setAdversarialError(null);
                  setAdversarialTest({ phase: "loading" });
                  try {
                    const apiBase = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
                    const res = await fetch(`${apiBase}/api/voice/adversarial-test`, {
                      method: "POST",
                      headers: { "x-session-id": sessionId },
                    });
                    const data = await res.json() as { success?: boolean; excerpts?: Array<{ id: number; text: string }>; explanation?: string; error?: string };
                    if (!res.ok || !data.success) throw new Error(data.error || "Failed to generate test");
                    setAdversarialTest({ phase: "active", excerpts: data.excerpts!, explanation: data.explanation! });
                  } catch (err) {
                    setAdversarialError(err instanceof Error ? err.message : "Something went wrong");
                    setAdversarialTest({ phase: "idle" });
                  }
                }}
              >
                <ShieldCheck className="w-4 h-4" /> Start Voice Test
              </Button>
            )}

            {adversarialTest.phase === "loading" && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating test excerpts...
              </div>
            )}

            {adversarialTest.phase === "active" && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Which excerpt sounds most like you?</p>
                {adversarialTest.excerpts.map((excerpt, idx) => (
                  <button
                    key={excerpt.id}
                    onClick={async () => {
                      const explanation = adversarialTest.explanation;
                      try {
                        const apiBase = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
                        const res = await fetch(`${apiBase}/api/voice/adversarial-test/submit`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "x-session-id": sessionId },
                          body: JSON.stringify({ selectedIndex: idx }),
                        });
                        const data = await res.json() as { correct?: boolean; correctIndex?: number; weakAreas?: string[]; stats?: { attempts: number; correct: number }; error?: string };
                        if (!res.ok) throw new Error(data.error || "Submission failed");
                        setAdversarialTest({ phase: "result", correct: data.correct!, weakAreas: data.weakAreas ?? [], stats: data.stats!, selectedIndex: idx, correctIndex: data.correctIndex!, explanation });
                      } catch (err) {
                        setAdversarialError(err instanceof Error ? err.message : "Submission failed");
                      }
                    }}
                    className="w-full text-left p-4 rounded-xl border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-all text-sm leading-relaxed"
                  >
                    <span className="inline-block w-5 h-5 rounded-full bg-secondary text-xs font-bold text-muted-foreground text-center leading-5 mr-2">{String.fromCharCode(65 + idx)}</span>
                    {excerpt.text}
                  </button>
                ))}
              </div>
            )}

            {adversarialTest.phase === "result" && (
              <div className="space-y-4">
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${adversarialTest.correct ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${adversarialTest.correct ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
                    {adversarialTest.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${adversarialTest.correct ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                      {adversarialTest.correct ? "You nailed it!" : "Not quite — your voice tricked you"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      The correct excerpt was <strong>Option {String.fromCharCode(65 + adversarialTest.correctIndex)}</strong>.
                    </p>
                    {adversarialTest.explanation && (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed italic">{adversarialTest.explanation}</p>
                    )}
                  </div>
                </div>
                {adversarialTest.weakAreas.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Voice dimensions to watch</p>
                    <div className="flex flex-wrap gap-1.5">
                      {adversarialTest.weakAreas.map((area, i) => (
                        <span key={i} className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-500 text-xs font-medium">{area}</span>
                      ))}
                    </div>
                  </div>
                )}
                {adversarialTest.stats && (
                  <p className="text-xs text-muted-foreground">
                    Your score: {adversarialTest.stats.correct}/{adversarialTest.stats.attempts} correct
                  </p>
                )}
                <button
                  onClick={() => { setAdversarialTest({ phase: "idle" }); setAdversarialError(null); }}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RefreshCw className="w-3 h-3" /> Try again
                </button>
              </div>
            )}
          </div>
        )}

        <div className="bg-card rounded-3xl p-6 border border-border shadow-xl">
          <h3 className="font-display font-bold text-xl flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            Voice DNA
          </h3>

          {isLoading ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-secondary rounded w-2/3" />
              <div className="h-4 bg-secondary rounded w-1/2" />
              <div className="h-20 bg-secondary rounded-xl w-full" />
            </div>
          ) : profile && analysis ? (
            <div className="space-y-5">
              <VoiceAccuracyMeter sampleCount={profile.sampleCount ?? 0} />

              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-2xl">
                {profile.hasEnoughSamples ? <CheckCircle2 className="w-7 h-7 text-emerald-500" /> : <AlertCircle className="w-7 h-7 text-amber-500" />}
                <div>
                  <p className="font-semibold text-sm">{profile.sampleCount} Samples Learned</p>
                  <p className="text-xs text-muted-foreground">
                    {(profile.sampleCount ?? 0) - audioTranscripts.length} text{audioTranscripts.length > 0 ? ` + ${audioTranscripts.length} audio` : ""}
                    {" \u00B7 "}{profile.hasEnoughSamples ? "Ready for generation" : "Need more data"}
                  </p>
                </div>
              </div>

              {/* Voice Modes — shown when clustering has produced states */}
              {(() => {
                const voiceStates = (analysis as Record<string, unknown>).voiceStates as Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;
                if (!voiceStates || voiceStates.length === 0) return null;
                return (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <Zap className="w-3 h-3" /> Your Voice Modes
                    </div>
                    <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
                      Your writing clusters into distinct modes. Select one in the post generator to tune AI output to that register.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {voiceStates.map((vs) => (
                        <div
                          key={vs.label}
                          title={`${vs.description} — Best for: ${vs.exampleContext}`}
                          className="group relative"
                        >
                          <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-primary/8 border-primary/30 text-primary cursor-default select-none block">
                            {vs.label}
                          </span>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 p-2.5 rounded-xl bg-popover border border-border shadow-lg text-[10px] text-muted-foreground leading-snug hidden group-hover:block z-10 pointer-events-none">
                            <p className="font-semibold text-foreground mb-1">{vs.dominantTone}</p>
                            <p className="mb-1">{vs.description}</p>
                            <p className="text-muted-foreground/70">Best for: {vs.exampleContext}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {analysis.writingPersonality && analysis.writingPersonality.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Personality</span>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.writingPersonality.map((tag: string, i: number) => (
                      <span key={i} className="px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full capitalize">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cadence</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Punchy</span>
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500"
                      style={{ width: `${analysis.cadenceScore ?? 50}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">Flowing</span>
                </div>
              </div>

              <div className="space-y-3 pt-3 border-t border-border">
                {([
                  { label: "Primary Tone", value: analysis.tone },
                  { label: "Vocabulary", value: analysis.vocabularyComplexity },
                  { label: "Emoji Usage", value: analysis.emojiUsage },
                  { label: "Humor", value: analysis.humorLevel },
                  { label: "Story Pattern", value: analysis.storyPattern },
                  ...(analysis.sentenceRhythm ? [{ label: "Sentence Rhythm", value: analysis.sentenceRhythm }] : []),
                  ...(analysis.formattingPersonality ? [{ label: "Formatting", value: analysis.formattingPersonality }] : []),
                ] as { label: string; value: string }[]).map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-start gap-2 text-sm">
                    <div className="flex flex-col min-w-0">
                      <span className="text-muted-foreground">{label}</span>
                      {DNA_DESCRIPTIONS[label] && (
                        <span className="text-[10px] text-muted-foreground/60 leading-tight mt-0.5 max-w-[180px]">{DNA_DESCRIPTIONS[label]}</span>
                      )}
                    </div>
                    <span className="font-medium capitalize text-right max-w-[55%] text-xs leading-snug shrink-0">{value}</span>
                  </div>
                ))}
              </div>

              {analysis.hookTaxonomy && (
                <div className="space-y-2 pt-3 border-t border-border">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <Zap className="w-3 h-3" /> Hook Style
                  </div>
                  <p className="text-sm text-foreground">{analysis.hookTaxonomy}</p>
                  {analysis.hookExamples && analysis.hookExamples.length > 0 && (
                    <div className="space-y-1 mt-2">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Example Openers</span>
                      {analysis.hookExamples.map((ex: string, i: number) => (
                        <p key={i} className="text-xs text-foreground/80 pl-3 border-l-2 border-primary/30 italic">{ex}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {analysis.ctaPattern && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <Target className="w-3 h-3" /> CTA Pattern
                  </div>
                  <p className="text-sm text-foreground">{analysis.ctaPattern}</p>
                </div>
              )}

              {analysis.signaturePhrases && analysis.signaturePhrases.length > 0 && (
                <div className="space-y-2 pt-3 border-t border-border">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <MessageSquareQuote className="w-3 h-3" /> Signature Phrases
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.signaturePhrases.map((phrase: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-md">"{phrase}"</span>
                    ))}
                  </div>
                </div>
              )}

              {analysis.powerWords && analysis.powerWords.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Power Words</span>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.powerWords.map((word: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-md">{word}</span>
                    ))}
                  </div>
                </div>
              )}

              {analysis.punctuationFingerprint && (
                <div className="space-y-1.5 pt-3 border-t border-border">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Punctuation Fingerprint</span>
                  <p className="text-xs text-foreground/80 leading-relaxed">{analysis.punctuationFingerprint}</p>
                </div>
              )}

              {analysis.sentenceFragments && analysis.sentenceFragments.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quirky Fragments</span>
                  <div className="space-y-1">
                    {analysis.sentenceFragments.map((frag: string, i: number) => (
                      <p key={i} className="text-xs text-foreground/80 pl-3 border-l-2 border-primary/35 italic">"{frag}"</p>
                    ))}
                  </div>
                </div>
              )}

              {analysis.selfInterruptions && analysis.selfInterruptions.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Self-Interruptions</span>
                  <div className="space-y-1">
                    {analysis.selfInterruptions.map((si: string, i: number) => (
                      <p key={i} className="text-xs text-foreground/80 pl-3 border-l-2 border-amber-400/40 italic">"{si}"</p>
                    ))}
                  </div>
                </div>
              )}

              {analysis.typingFeel && analysis.typingFeel !== "mixed" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Typing Feel</span>
                  <span className="font-medium capitalize text-xs">{analysis.typingFeel}</span>
                </div>
              )}

              {analysis.fillerWordCadence && analysis.fillerWordCadence !== "none detected" && (
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Verbal Tics</span>
                  <p className="text-xs text-foreground/80">{analysis.fillerWordCadence}</p>
                </div>
              )}

              {analysis.capitalizationQuirks && analysis.capitalizationQuirks !== "standard" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Capitalization</span>
                  <span className="font-medium text-right max-w-[60%] text-xs leading-snug">{analysis.capitalizationQuirks}</span>
                </div>
              )}

              {analysis.characteristicOpeners && analysis.characteristicOpeners.length > 0 && (
                <div className="space-y-2 pt-3 border-t border-border">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <span>Characteristic Openers</span>
                    <span className="normal-case font-normal text-muted-foreground/50">— verbatim from your samples</span>
                  </div>
                  <div className="space-y-1">
                    {analysis.characteristicOpeners.map((opener: string, i: number) => (
                      <p key={i} className="text-xs text-foreground/80 pl-3 border-l-2 border-green-400/40 italic">"{opener}"</p>
                    ))}
                  </div>
                </div>
              )}

              {analysis.openingPattern && (
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Opening Pattern</span>
                  <p className="text-xs text-foreground/80 leading-relaxed">{analysis.openingPattern}</p>
                </div>
              )}

              {analysis.thoughtStarters && (analysis.thoughtStarters as string[]).length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <span>Thought Starters</span>
                    <span className="normal-case font-normal text-muted-foreground/50">— phrases you reach for</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(analysis.thoughtStarters as string[]).map((starter: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-md text-foreground/80 italic">"{starter}"</span>
                    ))}
                  </div>
                </div>
              )}

              {analysis.characteristicClosers && analysis.characteristicClosers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Characteristic Closers</span>
                  <div className="space-y-1">
                    {analysis.characteristicClosers.map((closer: string, i: number) => (
                      <p key={i} className="text-xs text-foreground/80 pl-3 border-l-2 border-blue-400/40 italic">"{closer}"</p>
                    ))}
                  </div>
                </div>
              )}


              {/* Reset profile button */}
              {!resetConfirmOpen ? (
                <div className="pt-4 border-t border-border/50">
                  <button
                    onClick={() => setResetConfirmOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset voice profile
                  </button>
                </div>
              ) : (
                <div className="pt-4 border-t border-destructive/30 bg-destructive/5 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-destructive">Reset voice profile?</p>
                  <p className="text-xs text-muted-foreground leading-snug">
                    This will permanently delete all your voice samples and AI analysis. Your voice preferences will be kept. This cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => resetMutation.mutate()}
                      disabled={resetMutation.isPending}
                      className="gap-1.5"
                    >
                      {resetMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Yes, reset
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setResetConfirmOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BrainCircuit className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No profile data yet. Train the AI to see your Voice DNA.</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>

    <div className="max-w-5xl mx-auto mt-8">
      <VoicePreferencesSection />
    </div>

    <div className="fixed bottom-28 md:bottom-8 right-4 md:right-6 z-50">
      <button
        type="button"
        onClick={() => navigate("/studio")}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold bg-primary text-primary-foreground shadow-[0_4px_24px_hsl(188_90%_42%/0.35)] hover:bg-primary/90 hover:shadow-[0_4px_32px_hsl(188_90%_42%/0.5)] transition-all duration-200 active:scale-95"
      >
        <Sparkles className="w-4 h-4" />
        Start Creating
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
    </>
  );
}
